// File: apps/web/src/features/import/StagedDetail.tsx
// Version: 2026.4.10 | AI classification card + transcript viewer + accept suggestion
//
// v2026.4.10: Added AI classification card (summary, confidence, domains, R&D),
//             conversation transcript viewer, and "Accept AI Suggestion" button.
// v2026.4.7:  Sprint 7 Wave 3 — initial slide-in drawer

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Markdown from 'react-markdown';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../../components/engine/tokens';
import { SectionLabel, StatusBadge } from '../../components/engine/EnginePageShell';
import {
  useStagedDetail,
  useUpdateMapping,
  useCommitImport,
  useIgnoreStaged,
  useTranscript,
  useAcceptSuggestion,
  useRDClassificationOverride,
} from '../../lib/api/import';
import { useProjects } from '../../lib/api/projects';
import { WorkItemSelector } from './WorkItemSelector';

interface StagedDetailProps {
  stagedId:  number;
  onClose:   () => void;
  onMutated: () => void;
}

export function StagedDetail({ stagedId, onClose, onMutated }: StagedDetailProps) {
  const { data: row, loading, error, refetch } = useStagedDetail(stagedId);
  const { update,  loading: saving     } = useUpdateMapping();
  const { commit,  loading: committing  } = useCommitImport();
  const { ignore,  loading: ignoring    } = useIgnoreStaged();
  const { accept,  loading: accepting   } = useAcceptSuggestion();

  const [workItemId, setWorkItemId] = useState<number | null>(null);
  const [notes,      setNotes]      = useState('');
  const [actionMsg,  setActionMsg]  = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);

  useEffect(() => {
    if (row) {
      setWorkItemId(row.mappedWorkItemId);
      setNotes('');
    }
  }, [row]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleSaveMapping = async () => {
    if (!row) return;
    if (!workItemId) {
      setActionMsg('Select a work item first. Create one in Projects if none exist.');
      return;
    }
    setActionMsg(null);
    const updated = await update(row.id, { workItemId, notes: notes || null });
    if (updated) {
      setActionMsg('Mapping saved — status changed to Mapped');
      await refetch();
      onMutated();
    }
  };

  const handleAcceptSuggestion = async () => {
    if (!row) return;
    setActionMsg(null);
    const result = await accept(row.id);
    if (result) {
      setActionMsg('AI suggestion accepted — mapped to work item');
      setWorkItemId(result.mappedWorkItemId);
      await refetch();
      onMutated();
    }
  };

  const handleCommit = async () => {
    if (!row) return;
    setActionMsg(null);
    const result = await commit({ ids: [row.id] });
    if (result) {
      const item = result.items[0];
      if (item?.success) {
        setActionMsg(`Committed as session #${item.sessionId}`);
        await refetch();
        onMutated();
      } else {
        setActionMsg(`Commit failed: ${item?.error ?? 'unknown error'}`);
      }
    }
  };

  const handleIgnore = async () => {
    if (!row) return;
    setActionMsg(null);
    const ok = await ignore(row.id, { reason: notes || undefined });
    if (ok) {
      setActionMsg('Marked as ignored');
      await refetch();
      onMutated();
    }
  };

  const isCommitted = row?.status === 'Committed';
  const isIgnored   = row?.status === 'Ignored';
  const isFrozen    = isCommitted || isIgnored;
  const canCommit   = row && row.status === 'Mapped';
  const hasAISuggestion = row?.aiSuggestedWorkItemId != null;
  const isClassified = row?.classificationStatus === 'Classified';

  return createPortal(
    <div style={drawerOverlayStyle} onClick={onClose}>
      <div
        style={drawerPanelStyle}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SectionLabel color={C.cyan}>STAGED CONVERSATION · #{stagedId}</SectionLabel>
            <div style={{
              fontSize: 20, fontWeight: 600, color: C.text,
              marginTop: 4, fontFamily: F.display, wordBreak: 'break-word',
            }}>
              {row?.title ?? '…'}
            </div>
          </div>
          <button onClick={onClose} style={closeButtonStyle}>✕</button>
        </div>

        {/* Body */}
        <div style={bodyStyle}>
          {loading && <div style={{ color: C.muted }}>Loading…</div>}
          {error && (
            <div style={{ color: C.coral, fontSize: 13 }}>
              Failed to load: {error.message}
            </div>
          )}

          {row && (
            <>
              {/* Status pills */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                <StatusBadge label={row.status} color={statusColor(row.status)} />
                <StatusBadge
                  label={row.classificationStatus}
                  color={classificationStatusColor(row.classificationStatus)}
                />
                {row.isEstimated && <StatusBadge label="ESTIMATED" color={C.amber} />}
                {row.committedSessionId != null && (
                  <StatusBadge label={`SESSION #${row.committedSessionId}`} color={C.neon} />
                )}
              </div>

              {/* Source metadata */}
              <div style={metadataGridStyle}>
                <Field label="Source"      value={row.sourceType} />
                <Field label="Messages"    value={row.messageCount.toString()} />
                <Field label="Active time" value={`${row.estimatedDurationMinutes}m`} />
                <Field label="Est. cost"   value={`$${row.estimatedCostUSD.toFixed(4)}`} />
                <Field label="Created"     value={new Date(row.sourceCreatedAt).toLocaleString()} />
                <Field label="Updated"     value={new Date(row.sourceUpdatedAt).toLocaleString()} />
              </div>

              {/* ── AI Classification Card ─────────────────────────────── */}
              {isClassified && (
                <div style={{ marginTop: 20 }}>
                  <SectionLabel color={C.violet}>AI CLASSIFICATION</SectionLabel>
                  <div style={classificationCardStyle}>
                    {/* Confidence bar */}
                    {row.classificationConfidence != null && (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: 11, color: C.dim, fontFamily: F.display }}>
                            CONFIDENCE
                          </span>
                          <span style={{
                            fontSize: 13, fontWeight: 600, fontFamily: F.mono,
                            color: confidenceColor(row.classificationConfidence),
                          }}>
                            {(row.classificationConfidence * 100).toFixed(0)}%
                          </span>
                        </div>
                        <div style={{
                          height: 6, borderRadius: 3,
                          background: C.faint, overflow: 'hidden',
                        }}>
                          <div style={{
                            height: '100%', borderRadius: 3,
                            width: `${Math.min(100, row.classificationConfidence * 100)}%`,
                            background: confidenceColor(row.classificationConfidence),
                            transition: 'width 0.4s ease',
                          }} />
                        </div>
                      </div>
                    )}

                    {/* AI suggested project */}
                    {row.aiSuggestedProjectName && (
                      <div style={{ marginBottom: 10 }}>
                        <span style={{ fontSize: 11, color: C.dim, fontFamily: F.display }}>
                          SUGGESTED PROJECT:{' '}
                        </span>
                        <span style={{ fontSize: 13, color: C.cyan, fontWeight: 600, fontFamily: F.display }}>
                          {row.aiSuggestedProjectName}
                        </span>
                      </div>
                    )}

                    {/* Summary */}
                    {row.aiGeneratedSummary && (
                      <div style={{
                        fontSize: 13, color: C.textSoft, lineHeight: 1.5,
                        fontFamily: F.display, marginBottom: 12,
                      }}>
                        <Markdown components={mdComponents}>{row.aiGeneratedSummary}</Markdown>
                      </div>
                    )}

                    {/* Expertise domain tags */}
                    {row.aiExpertiseDomains && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                        {parseDomains(row.aiExpertiseDomains).map(tag => (
                          <span key={tag} style={domainTagStyle}>{tag}</span>
                        ))}
                      </div>
                    )}

                    {/* R&D qualification (with override).
                        Three states:
                          • qualifies = true  → green verdict
                          • qualifies = false → dim verdict
                          • qualifies = null  → coral "Needs Review" — the
                            null-on-suspicious safety net tripped because
                            the classifier returned zero evidence on a
                            substantive session. Treat as unclassified,
                            not as a silent rejection.
                        Every classified row gets an Override button at
                        the bottom so reviewers can correct wrong verdicts
                        as well as resolve needs-review rows. */}
                    {row.classificationStatus === 'Classified' && (
                      <RDQualificationCard
                        stagedId={row.id}
                        qualifies={row.aiQualifiesForRDCredit}
                        rationale={row.aiRDRationale}
                        onOverridden={async () => {
                          await refetch();
                          onMutated();
                        }}
                      />
                    )}

                    {/* Classification cost */}
                    {row.classificationCostUSD != null && (
                      <div style={{ fontSize: 11, color: C.dim, fontFamily: F.mono, marginTop: 8 }}>
                        Classification cost: ${row.classificationCostUSD.toFixed(4)}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── Transcript viewer ──────────────────────────────────── */}
              <div style={{ marginTop: 20 }}>
                <button
                  onClick={() => setShowTranscript(!showTranscript)}
                  style={transcriptToggleStyle}
                >
                  <span>{showTranscript ? '▾' : '▸'} Conversation Transcript</span>
                  <span style={{ fontSize: 11, color: C.dim, fontFamily: F.mono }}>
                    {row.messageCount} messages
                  </span>
                </button>
                {showTranscript && <TranscriptViewer stagedId={stagedId} />}
              </div>

              {/* First message preview (when transcript is hidden) */}
              {!showTranscript && row.firstUserMessagePreview && (
                <div style={{ marginTop: 12 }}>
                  <SectionLabel>FIRST USER MESSAGE</SectionLabel>
                  <div style={previewBoxStyle}>
                    {row.firstUserMessagePreview}
                  </div>
                </div>
              )}

              {/* ── Accept AI Suggestion ───────────────────────────────── */}
              {!isFrozen && hasAISuggestion && row.status === 'Unmapped' && (
                <div style={{ marginTop: 20 }}>
                  <div style={acceptSuggestionCardStyle}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.violet, fontFamily: F.display }}>
                        AI suggests mapping to:
                      </div>
                      <div style={{ fontSize: 14, color: C.text, fontFamily: F.display, marginTop: 4 }}>
                        {row.aiSuggestedWorkItemTitle ?? `Work item #${row.aiSuggestedWorkItemId}`}
                      </div>
                      {row.aiSuggestedProjectName && (
                        <div style={{ fontSize: 12, color: C.muted, fontFamily: F.display, marginTop: 2 }}>
                          in {row.aiSuggestedProjectName}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleAcceptSuggestion}
                      disabled={accepting}
                      style={acceptButtonStyle(accepting)}
                    >
                      {accepting ? '…' : 'Accept'}
                    </button>
                  </div>
                </div>
              )}

              {/* ── Project + Work Item mapping ────────────────────────── */}
              <div style={{ marginTop: 20 }}>
                <SectionLabel>MAP TO PROJECT &amp; WORK ITEM</SectionLabel>
                <div style={{ marginTop: 8 }}>
                  {isFrozen ? (
                    <div style={frozenBoxStyle}>
                      {row.mappedWorkItemTitle ?? 'No work item mapped'}
                      <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>
                        Mapping is frozen because this row is {row.status.toLowerCase()}.
                      </div>
                    </div>
                  ) : (
                    <ProjectWorkItemPicker
                      workItemId={workItemId}
                      onWorkItemChange={setWorkItemId}
                      aiSuggestedWorkItemId={row.aiSuggestedWorkItemId}
                      aiSuggestedProjectId={row.aiSuggestedProjectId}
                    />
                  )}
                </div>
              </div>

              {/* Notes */}
              {!isFrozen && (
                <div style={{ marginTop: 20 }}>
                  <SectionLabel>NOTES (OPTIONAL)</SectionLabel>
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Add notes that propagate to the Session record on commit…"
                    style={textareaStyle}
                  />
                </div>
              )}

              {/* Workflow hint when blocked */}
              {!isFrozen && !workItemId && (
                <div style={{
                  marginTop: 16, padding: '10px 14px', borderRadius: 6,
                  background: `${C.amber}08`, border: `1px solid ${C.amber}28`,
                  fontSize: 12, color: C.amber, fontFamily: F.display,
                }}>
                  Select a work item to enable Save &amp; Commit. If none exist, create one in the Projects page first.
                </div>
              )}

              {/* Action message */}
              {actionMsg && <div style={actionMsgStyle}>{actionMsg}</div>}
            </>
          )}
        </div>

        {/* Footer */}
        {row && !isFrozen && (
          <div style={footerStyle}>
            <button
              onClick={handleIgnore}
              disabled={ignoring || saving || committing || accepting}
              style={ignoreButtonStyle(ignoring)}
              title="Mark as ignored — preserves the dedupe lock so re-imports skip this row"
            >
              {ignoring ? 'Ignoring…' : 'Ignore'}
            </button>

            <div style={{ flex: 1 }} />

            <button
              onClick={handleSaveMapping}
              disabled={saving || committing || ignoring || accepting}
              style={secondaryButtonStyle(saving)}
            >
              {saving ? 'Saving…' : 'Save mapping'}
            </button>

            <button
              onClick={handleCommit}
              disabled={!canCommit || committing || saving || ignoring || accepting}
              style={primaryButtonStyle(!canCommit || committing)}
              title={canCommit
                ? 'Create a Session row from this conversation'
                : 'Map a work item first to enable commit'}
            >
              {committing ? 'Committing…' : 'Commit → Session'}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ─── Project + Work Item picker ───────────────────────────────────────────

function ProjectWorkItemPicker({
  workItemId,
  onWorkItemChange,
  aiSuggestedWorkItemId,
  aiSuggestedProjectId,
}: {
  workItemId: number | null;
  onWorkItemChange: (id: number | null) => void;
  aiSuggestedWorkItemId: number | null;
  aiSuggestedProjectId: number | null;
}) {
  const { data: projects } = useProjects();
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    aiSuggestedProjectId
  );

  // Resolve the selected project name for filtering work items
  const selectedProjectName = projects?.find(p => p.id === selectedProjectId)?.name ?? null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Project dropdown */}
      <div>
        <div style={{
          fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
          color: C.dim, marginBottom: 6, fontFamily: F.display,
        }}>
          PROJECT
          {aiSuggestedProjectId != null && selectedProjectId === aiSuggestedProjectId && (
            <span style={{
              marginLeft: 8, fontSize: 9, color: C.violet, fontWeight: 700,
              padding: '1px 5px', borderRadius: 3,
              background: `${C.violet}14`, border: `1px solid ${C.violet}38`,
            }}>
              AI SUGGESTED
            </span>
          )}
        </div>
        <select
          value={selectedProjectId ?? ''}
          onChange={e => {
            const val = e.target.value ? Number(e.target.value) : null;
            setSelectedProjectId(val);
            onWorkItemChange(null);
          }}
          style={selectStyle}
        >
          <option value="">All projects (no filter)</option>
          {projects?.map(p => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.id === aiSuggestedProjectId ? ' ⟵ AI' : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Work item picker (filtered by project when selected) */}
      <WorkItemSelector
        value={workItemId}
        onChange={onWorkItemChange}
        aiSuggestedId={aiSuggestedWorkItemId}
        filterProjectId={selectedProjectId}
        filterProjectName={selectedProjectName}
      />
    </div>
  );
}

// ─── Transcript sub-component ─────────────────────────────────────────────

function TranscriptViewer({ stagedId }: { stagedId: number }) {
  const { data, loading, error } = useTranscript(stagedId);

  if (loading) {
    return <div style={{ padding: 16, color: C.muted, fontSize: 13 }}>Loading transcript…</div>;
  }
  if (error) {
    return <div style={{ padding: 16, color: C.coral, fontSize: 13 }}>Failed to load transcript</div>;
  }

  const messages = Array.isArray(data?.messages) ? data.messages : [];
  if (messages.length === 0) {
    return (
      <div style={{ padding: 16, color: C.dim, fontSize: 13, fontStyle: 'italic' }}>
        No transcript available for this conversation.
      </div>
    );
  }

  return (
    <div style={transcriptContainerStyle}>
      {messages.map((msg, i) => {
        const role = typeof msg.role === 'string' ? msg.role : 'unknown';
        const raw = typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content ?? '');
        // Strip Claude export placeholder for unsupported blocks (tool use, images, etc.)
        const content = raw
          .replace(/This block is not supported on your current device yet\.\s*/g, '')
          .trim();
        if (!content) return null;
        return (
          <div key={i} style={messageStyle(role)}>
            <div style={messageRoleLabelStyle(role)}>
              {role === 'human' ? 'You' : 'Assistant'}
            </div>
            <div style={messageContentStyle}>
              <Markdown components={mdComponents}>{content}</Markdown>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function parseDomains(json: string): string[] {
  try {
    const arr = JSON.parse(json);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{
        fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
        color: C.dim, marginBottom: 4, fontFamily: F.display,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 13, color: C.text, fontFamily: F.mono, wordBreak: 'break-word',
      }}>
        {value}
      </div>
    </div>
  );
}

// ─── Color helpers ────────────────────────────────────────────────────────

function statusColor(status: string): string {
  switch (status) {
    case 'Mapped':    return C.cyan;
    case 'Committed': return C.neon;
    case 'Ignored':   return C.dim;
    default:          return C.amber;
  }
}

function classificationStatusColor(status: string): string {
  switch (status) {
    case 'Classified':       return C.violet;
    case 'Classifying':      return C.cyan;
    case 'Failed':           return C.coral;
    case 'ContentFiltered':  return C.coral;
    case 'Skipped':          return C.dim;
    default:                 return C.amber;  // Pending
  }
}

function confidenceColor(confidence: number): string {
  if (confidence >= 0.85) return C.neon;
  if (confidence >= 0.6)  return C.amber;
  return C.coral;
}

// ─── Styles ───────────────────────────────────────────────────────────────

const drawerOverlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0,
  background: 'rgba(0, 0, 0, 0.65)', backdropFilter: 'blur(4px)',
  display: 'flex', justifyContent: 'flex-end', zIndex: 9999,
};

const drawerPanelStyle: React.CSSProperties = {
  width: 'min(720px, 100vw)', height: '100vh',
  background: C.panel, borderLeft: `1px solid ${C.neon}38`,
  boxShadow: `-32px 0 64px rgba(0, 0, 0, 0.5)`,
  display: 'flex', flexDirection: 'column', fontFamily: F.display,
};

const headerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 16,
  padding: '24px 24px 16px', borderBottom: `1px solid ${C.faint}`,
};

const closeButtonStyle: React.CSSProperties = {
  width: 32, height: 32, padding: 0, fontSize: 16,
  background: 'transparent', color: C.muted,
  border: `1px solid ${C.faint}`, borderRadius: 6,
  cursor: 'pointer', flexShrink: 0,
};

const bodyStyle: React.CSSProperties = {
  flex: 1, padding: 24, overflowY: 'auto',
};

const metadataGridStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 16, marginTop: 8,
};

const previewBoxStyle: React.CSSProperties = {
  marginTop: 8, padding: '12px 16px',
  background: C.surface, border: `1px solid ${C.faint}`, borderRadius: 8,
  fontSize: 13, color: C.muted, fontFamily: F.display,
  lineHeight: 1.55, maxHeight: 160, overflowY: 'auto', whiteSpace: 'pre-wrap',
};

const classificationCardStyle: React.CSSProperties = {
  marginTop: 8, padding: 16,
  background: `${C.violet}06`, border: `1px solid ${C.violet}28`,
  borderRadius: 8,
};

const domainTagStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, letterSpacing: '0.04em',
  color: C.cyan, padding: '3px 8px', borderRadius: 4,
  background: `${C.cyan}10`, border: `1px solid ${C.cyan}28`,
  fontFamily: F.mono,
};

const acceptSuggestionCardStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 16,
  padding: '14px 16px',
  background: `${C.violet}08`, border: `1px solid ${C.violet}38`,
  borderRadius: 8,
};

const acceptButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding: '8px 20px', fontSize: 13, fontWeight: 600,
  fontFamily: F.display, borderRadius: 6,
  background: disabled ? C.faint : C.violet,
  color: disabled ? C.dim : '#fff',
  border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
  boxShadow: disabled ? 'none' : `0 0 12px ${C.violet}40`,
  flexShrink: 0,
});

const transcriptToggleStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  width: '100%', padding: '10px 14px',
  background: C.surface, border: `1px solid ${C.faint}`, borderRadius: 8,
  color: C.text, fontSize: 13, fontWeight: 600, fontFamily: F.display,
  cursor: 'pointer',
};

const transcriptContainerStyle: React.CSSProperties = {
  marginTop: 8, maxHeight: 400, overflowY: 'auto',
  border: `1px solid ${C.faint}`, borderRadius: 8,
  background: C.surface,
};

const messageStyle = (role: string): React.CSSProperties => ({
  padding: '12px 16px',
  borderBottom: `1px solid ${C.faint}`,
  background: role === 'human' ? 'transparent' : `${C.violet}04`,
});

const messageRoleLabelStyle = (role: string): React.CSSProperties => ({
  fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
  textTransform: 'uppercase', marginBottom: 6,
  color: role === 'human' ? C.cyan : C.violet,
  fontFamily: F.display,
});

const messageContentStyle: React.CSSProperties = {
  fontSize: 13, color: C.textSoft, lineHeight: 1.55,
  fontFamily: F.display, wordBreak: 'break-word',
};

// Markdown component overrides for engine-themed rendering
const mdComponents: import('react-markdown').Components = {
  p: ({ children }) => (
    <p style={{ margin: '6px 0', lineHeight: 1.55 }}>{children}</p>
  ),
  h1: ({ children }) => (
    <h1 style={{ fontSize: 16, fontWeight: 700, color: C.text, margin: '12px 0 6px', fontFamily: F.display }}>{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 style={{ fontSize: 15, fontWeight: 700, color: C.text, margin: '10px 0 4px', fontFamily: F.display }}>{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 style={{ fontSize: 14, fontWeight: 600, color: C.text, margin: '8px 0 4px', fontFamily: F.display }}>{children}</h3>
  ),
  code: ({ children, className }) => {
    const isBlock = className?.startsWith('language-');
    return isBlock ? (
      <code style={{
        display: 'block', padding: '10px 14px', margin: '8px 0',
        background: C.surface, border: `1px solid ${C.faint}`, borderRadius: 6,
        fontSize: 12, fontFamily: F.mono, color: C.cyan, overflowX: 'auto',
        whiteSpace: 'pre',
      }}>{children}</code>
    ) : (
      <code style={{
        padding: '1px 5px', borderRadius: 3,
        background: `${C.violet}14`, color: C.violet,
        fontSize: '0.92em', fontFamily: F.mono,
      }}>{children}</code>
    );
  },
  pre: ({ children }) => <>{children}</>,
  ul: ({ children }) => (
    <ul style={{ margin: '6px 0', paddingLeft: 20 }}>{children}</ul>
  ),
  ol: ({ children }) => (
    <ol style={{ margin: '6px 0', paddingLeft: 20 }}>{children}</ol>
  ),
  li: ({ children }) => (
    <li style={{ margin: '2px 0' }}>{children}</li>
  ),
  a: ({ href, children }) => (
    <a href={href} style={{ color: C.cyan, textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">{children}</a>
  ),
  strong: ({ children }) => (
    <strong style={{ color: C.text, fontWeight: 600 }}>{children}</strong>
  ),
  blockquote: ({ children }) => (
    <blockquote style={{
      margin: '8px 0', padding: '4px 12px',
      borderLeft: `3px solid ${C.violet}58`, color: C.muted,
    }}>{children}</blockquote>
  ),
};

const selectStyle: React.CSSProperties = {
  width: '100%', padding: '10px 14px',
  fontSize: 14, fontFamily: F.display,
  color: C.text, background: C.surface,
  border: `1px solid ${C.faint}`, borderRadius: 8,
  outline: 'none', cursor: 'pointer',
};

const frozenBoxStyle: React.CSSProperties = {
  padding: '12px 16px', background: C.surface,
  border: `1px solid ${C.faint}`, borderRadius: 8,
  color: C.muted, fontSize: 14, fontFamily: F.display,
};

const textareaStyle: React.CSSProperties = {
  marginTop: 8, width: '100%', minHeight: 80,
  padding: '10px 14px', fontSize: 13, fontFamily: F.display,
  color: C.text, background: C.surface,
  border: `1px solid ${C.faint}`, borderRadius: 8,
  outline: 'none', resize: 'vertical',
};

const actionMsgStyle: React.CSSProperties = {
  marginTop: 16, padding: '10px 14px',
  background: `${C.cyan}08`, border: `1px solid ${C.cyan}38`,
  borderRadius: 6, color: C.cyan, fontSize: 13, fontFamily: F.display,
};

const footerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '16px 24px', borderTop: `1px solid ${C.faint}`,
  background: `${C.bg}88`,
};

const primaryButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding: '10px 18px', fontSize: 13, fontWeight: 600, fontFamily: F.display,
  background: disabled ? C.faint : C.neon,
  color: disabled ? C.dim : '#050910',
  border: 'none', borderRadius: 6,
  cursor: disabled ? 'not-allowed' : 'pointer',
  boxShadow: disabled ? 'none' : `0 0 16px ${C.neonGlow}`,
});

const secondaryButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding: '10px 18px', fontSize: 13, fontWeight: 500, fontFamily: F.display,
  background: 'transparent', color: C.cyan,
  border: `1px solid ${C.cyan}58`, borderRadius: 6,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
});

const ignoreButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding: '10px 18px', fontSize: 13, fontWeight: 500, fontFamily: F.display,
  background: 'transparent', color: C.dim,
  border: `1px solid ${C.faint}`, borderRadius: 6,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
});

// ─── R&D qualification card with inline override ─────────────────────────
//
// Renders the current R&D verdict (qualifies / doesn't / needs-review) and
// exposes an override form that posts to /api/v1/import/staged/{id}/rd-override.
// The override is audit-logged on the server — see ImportController.
//
// Three visual states for the verdict banner:
//   • qualifies === true  → green "Qualifies for R&D Credit"
//   • qualifies === false → dim "Not R&D Qualified"
//   • qualifies === null  → coral "⚠ Needs Review" (safety net tripped)

function RDQualificationCard({
  stagedId,
  qualifies,
  rationale,
  onOverridden,
}: {
  stagedId:    number;
  qualifies:   boolean | null;
  rationale:   string | null;
  onOverridden: () => void | Promise<void>;
}) {
  const { override, loading } = useRDClassificationOverride();
  const [editing, setEditing] = useState(false);
  const [newVerdict, setNewVerdict] = useState<boolean | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const needsReview = qualifies === null;

  const bg =
    qualifies === true  ? `${C.neon}08` :
    qualifies === false ? `${C.dim}08`  :
                          `${C.coral}0E`;
  const border =
    qualifies === true  ? `${C.neon}38`  :
    qualifies === false ? C.faint         :
                          `${C.coral}55`;
  const textColor =
    qualifies === true  ? C.neon  :
    qualifies === false ? C.dim   :
                          C.coral;
  const heading =
    qualifies === true  ? '✓ Qualifies for R&D Credit' :
    qualifies === false ? '✗ Not R&D Qualified'        :
                          '⚠ Needs Review — zero evidence on substantive session';

  const submit = async () => {
    if (newVerdict == null) { setError('Pick a verdict first.'); return; }
    if (reason.trim().length < 10) {
      setError('Rationale is required (at least 10 characters) — the audit trail depends on it.');
      return;
    }
    setError(null);
    const result = await override(stagedId, {
      qualifiesForRDCredit: newVerdict,
      rationale: reason.trim(),
    });
    if (result) {
      setEditing(false);
      setNewVerdict(null);
      setReason('');
      await onOverridden();
    } else {
      setError('Override failed. Try again or contact an admin if the error persists.');
    }
  };

  return (
    <div style={{
      padding: '10px 12px', borderRadius: 6,
      background: bg, border: `1px solid ${border}`,
    }}>
      <div style={{
        fontSize: 12, fontWeight: 600, fontFamily: F.display,
        color: textColor,
        marginBottom: rationale ? 4 : 0,
      }}>
        {heading}
      </div>
      {rationale && (
        <div style={{ fontSize: 12, color: C.muted, fontFamily: F.display, lineHeight: 1.45 }}>
          {rationale}
        </div>
      )}

      {!editing && (
        <div style={{ marginTop: 10 }}>
          <button
            onClick={() => {
              setEditing(true);
              setNewVerdict(needsReview ? null : !qualifies);
              setReason('');
              setError(null);
            }}
            style={{
              padding: '5px 12px', fontSize: 11, fontWeight: 600,
              fontFamily: F.mono, cursor: 'pointer',
              background: needsReview ? `${C.coral}18` : 'transparent',
              color: needsReview ? C.coral : C.muted,
              border: `1px solid ${needsReview ? C.coral + '55' : C.faint}`,
              borderRadius: 5,
            }}
          >
            {needsReview ? '⚑ Set R&D verdict manually' : '✎ Override R&D verdict'}
          </button>
        </div>
      )}

      {editing && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setNewVerdict(true)}
              style={verdictPillStyle(newVerdict === true, C.neon)}
            >
              ✓ Qualifies
            </button>
            <button
              onClick={() => setNewVerdict(false)}
              style={verdictPillStyle(newVerdict === false, C.coral)}
            >
              ✗ Does not qualify
            </button>
          </div>

          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Rationale — cite the specific evidence in the transcript that drives your verdict. This text is preserved in the audit trail and may appear in R&D credit documentation."
            rows={4}
            style={{
              width: '100%', padding: '8px 10px', fontSize: 12, fontFamily: F.display,
              background: C.bg, color: C.text,
              border: `1px solid ${C.faint}`, borderRadius: 6,
              resize: 'vertical', lineHeight: 1.45, outline: 'none',
            }}
          />

          {error && (
            <div style={{ fontSize: 11, color: C.coral, fontFamily: F.display }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={submit}
              disabled={loading}
              style={{
                padding: '7px 14px', fontSize: 12, fontWeight: 600,
                fontFamily: F.mono, cursor: loading ? 'not-allowed' : 'pointer',
                background: `${C.violet}18`, color: C.violet,
                border: `1px solid ${C.violet}55`, borderRadius: 5,
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? '◌ Saving...' : 'Save override'}
            </button>
            <button
              onClick={() => { setEditing(false); setError(null); }}
              disabled={loading}
              style={{
                padding: '7px 14px', fontSize: 12,
                fontFamily: F.mono, cursor: loading ? 'not-allowed' : 'pointer',
                background: 'transparent', color: C.muted,
                border: `1px solid ${C.faint}`, borderRadius: 5,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const verdictPillStyle = (selected: boolean, color: string): React.CSSProperties => ({
  padding: '6px 14px', fontSize: 12, fontWeight: 600, fontFamily: F.mono,
  cursor: 'pointer',
  background: selected ? `${color}22` : 'transparent',
  color: selected ? color : C.muted,
  border: `1px solid ${selected ? color : C.faint}`,
  borderRadius: 5,
});
